import { Button as StencilButton, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Chip as StencilChip, ChipBackgroundColor } from "@amzn/stencil-react-components/chip";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import { IconHeartFill } from "@amzn/stencil-react-icons";
import { Button } from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import Link, { LinkTextDecorationOptions } from "@amzn/uxm-stencil-react-components/lib/src/stencil/link";
import SearchField from "@amzn/uxm-stencil-react-components/lib/src/stencil/search-field";
import { Paths } from "common/paths/paths";
import { AiForMeURLQueryStrings } from "common/types/constants";
import { getString } from "common/uistringlabels/uiStringUtils";
import { config } from "config";
import { AmazonianInformation } from "lib/models/oui-models";
import { UserInformation } from "lib/services/user-information-service";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { AiForMeUseCases } from "./AiForMeUseCases";
import { AiForMeSearchListState } from "./useAiForMeSearchList";

const HANDLER_DEBOUNCE_TIMEOUT = 300;

export interface AiForMeHeaderBannerProps {
  inputs: AiForMeSearchListState;
  updateInputs: (key: keyof AiForMeSearchListState, value: any) => void;
}

export const AiForMeHeaderBanner = (props: AiForMeHeaderBannerProps) => {
  const { inputs, updateInputs } = props;
  const [userInfo, setUserInfo] = useState<AmazonianInformation | undefined>(undefined);
  const history = useHistory();

  const [localInputValue, setLocalInputValue] = useState("");

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearchTextChange = useCallback((query: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      updateInputs("searchText", query);
    }, HANDLER_DEBOUNCE_TIMEOUT);
  }, []);

  const updateQuery = async (query: string) => {
    setLocalInputValue(query);
    handleSearchTextChange(query);
    return [];
  };

  const getQueryValue = () => {
    const params = new URLSearchParams(location.search);
    const queryValue = params.get(AiForMeURLQueryStrings.QUERY);
    return queryValue;
  };

  useEffect(() => {
    const queryValue = getQueryValue();

    // Set search from URL params using batch update
    if (queryValue) {
      updateInputs("searchText", queryValue);
      updateInputs("searchInputValue", queryValue);
      setLocalInputValue(queryValue);
    }

    UserInformation.current
      .getUserInformation()
      .then((userInfo) => {
        setUserInfo(userInfo);
      })
      .catch((error) => {
        console.error("Error fetching user information:", error);
      });
  }, []);

  return (
    <>
      <Row justifyContent="space-between" alignItems="flex-end" style={{ minHeight: "350px" }}>
        <Col flex={2}>
          <Row padding={[20, 0, 20, 0]}>
            <StencilChip
              backgroundColor={ChipBackgroundColor.Pink}
              icon={<img src="/img/ant-design_smile-outlined.svg" alt="Smiley icon" />}
            >
              {getString("aiForMe.salute", { amazonianName: userInfo?.basicInfo.firstName })}
            </StencilChip>
          </Row>
          <Text data-cy="welcome-title" fontSize={"T500"} fontWeight="bold" style={{ paddingBottom: 20 }}>
            {getString("aiForMe.discoverAiToolsWithoutName")}
          </Text>
          {config.displayAiForMeUseCaseSuggestions && <AiForMeUseCases updateQuery={updateQuery}></AiForMeUseCases>}
          {!config.displayAiForMeUseCaseSuggestions && (
            <>
              <Text style={{ paddingBottom: 20 }}>
                {getString("aiForMe.findBestTools")}
                <br />
                {getString("aiForMe.boostProductivity")}
              </Text>
            </>
          )}
          <Row columnGap={20} padding={[0, 0, 18, 0]} width="75%">
            <SearchField
              type="text"
              data-testid="txt_search"
              defaultValue={inputs.searchInputValue}
              onChange={(query: string) => {
                updateQuery(query);
              }}
              touchpoint="search-ai-tools"
              placeholder="Search"
              shouldShowListOnNoResults={false}
              query={localInputValue}
            ></SearchField>
          </Row>
          <Row columnGap={20} padding={{ bottom: 45 }}>
            <Button
              variant={ButtonVariant.Primary}
              onClick={
                /* istanbul ignore next */
                () => {
                  history.push(Paths.aiformeIntake.url);
                }
              }
              touchpoint="contribute-a-tool"
            >
              {getString("aiForMe.contributeTool")}
            </Button>
            <Link
              href={getString("aiForMe.learnMoreAboutAILink")}
              target="_blank"
              textDecoration={LinkTextDecorationOptions.None}
              data-testid="learn-about-ai-button"
              touchpoint="learn-about-ai-button"
            >
              <StencilButton>{getString("aiForMe.learnAboutAI")}</StencilButton>
            </Link>
          </Row>
        </Col>
        <Col flex={1}>
          <img
            src="/img/aiforme-people-animated.svg"
            alt="Two people working using AI tools"
            style={{
              maxWidth: 468,
              width: "100%",
              height: "auto",
            }}
            data-testid="people-img"
          />
        </Col>
      </Row>
    </>
  );
};
